import { useMemo, useRef, useState } from 'react';
import { useConversas } from '@/conversas/ConversasProvider';
import AnexarMidia from './AnexarMidia';
import GravarAudio from './GravarAudio';
import { TIPOS_ACEITOS } from '@/conversas/midiaEnvio';
import { enviarMensagem, enviarMidia } from '@/services/conversasService';
import { CONVITE_ENVIO, estadoDoEnvio, mostraDono } from '@/conversas/estadoAtendimento';
import { subirMidiaDeSaida } from '@/services/midiaService';

/**
 * Erros traduzidos para quem está atendendo.
 *
 * O mapa VENCE sobre `e.message`, então só entra aqui o código em que a tela
 * sabe mais que o servidor. `unavailable` e `deadline-exceeded` ficam DE FORA de
 * propósito: desde a blindagem do envio, é o `canais.js` que redige esses dois
 * textos, e ele sabe se a falha foi ao conectar ou ao esperar — informação que a
 * tela não tem. Um texto fixo aqui apagaria a do servidor (foi o que acontecia:
 * o atendente lia 'Confira se "npm run emu" está no ar', instrução de
 * desenvolvedor numa tela de produção). Os fallbacks abaixo só valem se a
 * mensagem vier vazia.
 */
const FALLBACK_POR_CODIGO = {
  'functions/unavailable': 'Não foi possível conectar ao WhatsApp agora. Tente de novo em instantes.',
  'functions/deadline-exceeded': 'O WhatsApp demorou a responder. Tente de novo.',
  // Falha de transporte antes de chegar à callable: aí não há mensagem de
  // servidor nenhuma, porque não houve servidor.
  'functions/internal': 'Não foi possível falar com o servidor. Tente de novo.',
};

const MENSAGENS_ERRO = {
  'functions/failed-precondition':
    'O número desta conversa ainda não tem canal configurado. Enquanto a VPS não existir, use um número em modo simulado.',
  'functions/permission-denied': 'Seu papel não atende o espaço desta conversa.',
  // Falhas do Storage, que acontecem ANTES da callable: sem tradução, o
  // atendente lê "storage/unauthorized" e não sabe o que fazer.
  'storage/unauthorized': 'Sem permissão para subir o arquivo. Confira o tamanho (máx. 25 MB).',
  'storage/retry-limit-exceeded': 'A subida do arquivo demorou demais. Tente de novo.',
  'storage/canceled': 'Envio cancelado.',
};

/** O que mostrar na barra de erro: tradução da tela, texto do servidor, ou genérico. */
const explicar = (e, generico) =>
  MENSAGENS_ERRO[e?.code] ?? e?.message ?? FALLBACK_POR_CODIGO[e?.code] ?? generico;

/**
 * Caixa de envio. Alterna entre responder ao cliente e sussurrar para a equipe,
 * com respostas rápidas por atalho e @menção de colega.
 */
export default function Composer({ aoPedirAtendimento = null }) {
  const { clinicaId, conversa, respostas, equipe, atendente } = useConversas();
  /**
   * ★★★ A1/E1 — o espelho da guarda do servidor. ⚠ A autoridade é o servidor; isto existe para
   * a tela **explicar antes** em vez de deixar a pessoa escrever e levar erro depois.
   */
  const estado = estadoDoEnvio(conversa, atendente?.id);
  const travado = estado !== 'pode' && estado !== 'grupo';
  const [texto, setTexto] = useState('');
  const [interna, setInterna] = useState(false);
  const [mencoes, setMencoes] = useState([]);
  const [erro, setErro] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const campo = useRef(null);

  // Anexo escolhido e gravador aberto. Só um dos dois por vez: são dois modos
  // de mandar a mesma coisa, e abrir ambos deixaria dois botões "Enviar" na
  // tela sem dizer qual vale.
  const [anexo, setAnexo] = useState(null);
  const [gravando, setGravando] = useState(false);
  const seletor = useRef(null);

  /**
   * Sobe o arquivo e chama a callable.
   *
   * A ordem importa: primeiro o Storage, depois a função. Se a subida falhar,
   * nada foi enviado e nada foi gravado. Se a callable falhar depois, sobra um
   * arquivo órfão no bucket — o que é preferível a uma mensagem na conversa
   * apontando para um arquivo que não existe.
   */
  async function despacharMidia({ arquivo, nome, tipo, mimetype, legenda }) {
    setErro(null);
    setEnviando(true);
    try {
      const caminho = await subirMidiaDeSaida(clinicaId, conversa.id, arquivo, nome);
      await enviarMidia({
        clinicaId,
        conversaId: conversa.id,
        caminho,
        tipo,
        mimetype,
        nomeArquivo: nome,
        legenda,
      });
      setAnexo(null);
      setGravando(false);
    } catch (e) {
      console.error('[OdontoSDR] Falha ao enviar mídia:', e);
      setErro(explicar(e, 'Não foi possível enviar o arquivo.'));
    } finally {
      setEnviando(false);
    }
  }

  function escolherArquivo(evento) {
    const arquivo = evento.target.files?.[0];
    // Permite reescolher o MESMO arquivo depois de cancelar: sem isto o input
    // não dispara change para o mesmo nome.
    evento.target.value = '';
    if (!arquivo) return;
    setErro(null);
    setGravando(false);
    setAnexo(arquivo);
  }

  // Sugestões abrem ao digitar "/" no começo (respostas) ou "@" (menções).
  const sugestoes = useMemo(() => {
    const atual = texto.trimStart();
    if (atual.startsWith('/')) {
      const termo = atual.slice(1).toLowerCase();
      return {
        tipo: 'resposta',
        itens: respostas.filter(
          (r) =>
            r.atalho?.toLowerCase().includes(termo) || r.titulo?.toLowerCase().includes(termo),
        ),
      };
    }
    const ultimoArroba = texto.lastIndexOf('@');
    if (interna && ultimoArroba >= 0) {
      const termo = texto.slice(ultimoArroba + 1).toLowerCase();
      if (!termo.includes(' ')) {
        return {
          tipo: 'mencao',
          itens: equipe.filter((u) => (u.nome ?? '').toLowerCase().includes(termo)),
        };
      }
    }
    return { tipo: null, itens: [] };
  }, [texto, respostas, equipe, interna]);

  function usarResposta(resposta) {
    // O texto vai com as variáveis intactas: quem resolve é o servidor, com os
    // dados reais da conversa, para não depender do que o cliente conhece.
    setTexto(resposta.texto);
    campo.current?.focus();
  }

  function usarMencao(usuario) {
    const corte = texto.lastIndexOf('@');
    setTexto(`${texto.slice(0, corte)}@${usuario.nome} `);
    setMencoes((atuais) => [...new Set([...atuais, usuario.id])]);
    campo.current?.focus();
  }

  async function enviar(evento) {
    evento?.preventDefault();
    const conteudo = texto.trim();
    if (!conteudo || enviando) return;

    setErro(null);
    setEnviando(true);
    try {
      await enviarMensagem({
        clinicaId,
        conversaId: conversa.id,
        texto: conteudo,
        interna,
        mencoes: interna ? mencoes : [],
      });
      setTexto('');
      setMencoes([]);
    } catch (e) {
      setErro(explicar(e, 'Não foi possível enviar.'));
    } finally {
      setEnviando(false);
    }
  }

  function aoTeclar(e) {
    if (e.key !== 'Enter') return;

    // Enter puro envia — é o gesto de quem está atendendo rápido.
    const comModificador = e.shiftKey || e.altKey || e.ctrlKey || e.metaKey;
    if (!comModificador) {
      e.preventDefault();
      enviar();
      return;
    }

    // Shift+Enter o navegador já quebra a linha sozinho; deixar passar mantém
    // o desfazer (Cmd+Z) do próprio campo funcionando.
    if (e.shiftKey) return;

    // Option/Ctrl/Cmd + Enter NÃO quebram linha por conta própria num
    // <textarea> — o navegador ignora. Então a quebra é inserida à mão, na
    // posição do cursor, preservando qualquer trecho selecionado.
    e.preventDefault();
    const campoAtual = e.target;
    const inicio = campoAtual.selectionStart ?? texto.length;
    const fim = campoAtual.selectionEnd ?? inicio;
    const novo = `${texto.slice(0, inicio)}\n${texto.slice(fim)}`;
    setTexto(novo);

    // O cursor precisa ir para DEPOIS da quebra, e só depois do React
    // repintar o valor — daí o requestAnimationFrame. Sem isso ele volta
    // para o fim do texto e a pessoa perde o lugar.
    const posicao = inicio + 1;
    requestAnimationFrame(() => {
      campoAtual.selectionStart = posicao;
      campoAtual.selectionEnd = posicao;
    });
  }

  return (
    <form className={interna ? 'composer nota' : 'composer'} onSubmit={enviar}>
      <div className="composer-modo">
        <button
          type="button"
          className={interna ? 'modo' : 'modo ativo'}
          onClick={() => setInterna(false)}
        >
          Responder ao cliente
        </button>
        <button
          type="button"
          className={interna ? 'modo ativo nota' : 'modo'}
          onClick={() => setInterna(true)}
        >
          Nota interna
        </button>
        {/**
          * ⚠⚠ O AVISO DE 20/set DEIXOU DE SER VERDADE, e isto é reversão registrada.
          *
          * ★ Ele dizia *"Em atendimento por X — **responder assume a conversa**"*. ★★★ Desde
          * 09/out **responder NÃO assume**: ele é **recusado**. ⚠ Manter aquela frase seria
          * ensinar o contrário da regra.
          * ★ Agora, quando há outra pessoa, a tela diz **de quem é** — e o convite a assumir
          * aparece no lugar da caixa de texto, abaixo.
          */}
        {mostraDono(conversa) && conversa.responsavelId !== atendente.id && (
          <span className="composer-aviso">Em atendimento por {conversa.responsavelNome}</span>
        )}
      </div>

      {sugestoes.itens.length > 0 && (
        <ul className="sugestoes">
          {sugestoes.itens.slice(0, 6).map((item) =>
            sugestoes.tipo === 'resposta' ? (
              <li key={item.id}>
                <button type="button" onClick={() => usarResposta(item)}>
                  <strong>{item.atalho}</strong>
                  <span>{item.titulo}</span>
                </button>
              </li>
            ) : (
              <li key={item.id}>
                <button type="button" onClick={() => usarMencao(item)}>
                  <strong>@{item.nome}</strong>
                  <span>{item.papel}</span>
                </button>
              </li>
            ),
          )}
        </ul>
      )}

      {/**
        * ★★★ A1/E1 — O CONVITE NO LUGAR DA CAIXA (decisão do dono, item 1).
        *
        * ⚠⚠ Só no modo "Responder ao cliente": ★ a **nota interna continua liberada** nos quatro
        * estados, e travá-la impediria justamente quem está tentando ajudar de registrar o que
        * sabe.
        *
        * ★★ Por que SUBSTITUIR a caixa e não só desabilitar o botão: ⚠⚠ caixa habilitada é
        * convite a escrever — ★★★ e descobrir a regra **depois de digitar** é o pior momento
        * possível. ★ Aqui a tela diz o que fazer **antes**, com o botão ao lado.
        */}
      {travado && !interna ? (
        <div className="composer-travado" role="status">
          <span>{CONVITE_ENVIO[estado]?.texto ?? 'Para responder, assuma a conversa'}</span>
          <button
            type="button"
            className="btn-primario compacto"
            disabled={enviando}
            onClick={() => aoPedirAtendimento?.(estado)}
          >
            {CONVITE_ENVIO[estado]?.acao ?? 'Assumir'}
          </button>
        </div>
      ) : (
      <textarea
        ref={campo}
        rows={interna ? 3 : 2}
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        onKeyDown={aoTeclar}
        placeholder={
          interna
            ? 'Nota visível só para a equipe. Use @ para mencionar um colega.'
            : 'Mensagem para o cliente. Digite / para respostas rápidas.'
        }
        aria-label={interna ? 'Nota interna' : 'Mensagem para o cliente'}
      />
      )}

      {anexo && (
        <AnexarMidia
          arquivo={anexo}
          aoEnviar={despacharMidia}
          aoCancelar={() => setAnexo(null)}
          ocupado={enviando}
        />
      )}

      {gravando && (
        <GravarAudio
          aoConcluir={despacharMidia}
          aoCancelar={() => setGravando(false)}
          ocupado={enviando}
        />
      )}

      {erro && <p className="erro">{erro}</p>}

      <div className="composer-acoes">
        {/* Anexo e áudio só no modo "Responder ao cliente": nota interna é
            texto para a equipe, e o WhatsApp do paciente não entra nela.

            Em grupo funcionam igual (Leva C): o caminho de upload é o mesmo
            (`conversas/{id}/saida/`, que as storage.rules já aceitam com o id de
            grupo) e o servidor roteia para o `grupoJid`. */}
        {!interna && !travado && (
          <span className="composer-midia">
            <input
              ref={seletor}
              type="file"
              accept={TIPOS_ACEITOS}
              onChange={escolherArquivo}
              hidden
            />
            <button
              type="button"
              className="btn-icone"
              onClick={() => seletor.current?.click()}
              disabled={enviando}
              title="Anexar foto, vídeo ou documento"
              aria-label="Anexar arquivo"
            >
              📎
            </button>
            <button
              type="button"
              className={gravando ? 'btn-icone ativo' : 'btn-icone'}
              onClick={() => {
                setAnexo(null);
                setGravando((g) => !g);
              }}
              disabled={enviando}
              title="Gravar áudio"
              aria-label="Gravar áudio"
              aria-pressed={gravando}
            >
              🎤
            </button>
          </span>
        )}
        <span className="composer-dica">
          <strong>Enter</strong> envia · <strong>Shift</strong> ou{' '}
          <strong>Option</strong>+Enter pula linha
          {interna
            ? ' · esta nota não é enviada ao cliente'
            : ' · {{primeiro_nome}} é preenchido no envio'}
        </span>
        {/* ★ O botão de enviar desaparece junto: ⚠ deixá-lo visível sobre um convite a assumir
            daria dois caminhos para a mesma intenção. ★★ Nota interna mantém o seu. */}
        {(!travado || interna) && (
        <button type="submit" className="btn-primario compacto" disabled={enviando || !texto.trim()}>
          {enviando ? 'Enviando…' : interna ? 'Salvar nota' : 'Enviar'}
        </button>
        )}
      </div>
    </form>
  );
}
